import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { Button } from "@/components/ui/button";
import InsuranceBadge from "@/components/InsuranceBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Download, MapPin, Package, FileText, Truck, RefreshCw, Loader2, XCircle, CheckCircle2, Video, Play, ExternalLink, Edit2, Save, X } from "lucide-react";
import { formatDateIST } from "@/utils/dateUtils";
import { LRCopyInfo } from "@/components/LRCopyInfo";
import OrderEditHistory from "@/components/OrderEditHistory";
import { AppointmentEditRow, ReferenceEditRow } from "@/components/OrderInfoEditors";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STATUS_COLORS = {
  CREATED: "bg-blue-100 text-blue-800",
  MANIFESTED: "bg-purple-100 text-purple-800",
  PICKED_UP: "bg-yellow-100 text-yellow-800",
  LEFT_ORIGIN: "bg-orange-100 text-orange-800",
  REACH_DESTINATION: "bg-cyan-100 text-cyan-800",
  APPOINTMENT_SET: "bg-indigo-100 text-indigo-800",
  APPOINTMENT_UPDATED: "bg-indigo-100 text-indigo-800",
  APPOINTMENT_SCHEDULED: "bg-indigo-100 text-indigo-800",
  UNDEL_REATTEMPT: "bg-amber-100 text-amber-800",
  PART_DEL: "bg-lime-100 text-lime-800",
  OFD: "bg-teal-100 text-teal-800",
  DELIVERED: "bg-green-100 text-green-800",
  RETURNED_INTRANSIT: "bg-red-100 text-red-800",
  RECEIVED_AT_RETURN_CENTER: "bg-pink-100 text-pink-800",
  RETURN_OFD: "bg-rose-100 text-rose-800",
  RETURN_DELIVERED: "bg-violet-100 text-violet-800",
  NOT_PICKED: "bg-gray-100 text-gray-800",
  LOST: "bg-red-200 text-red-900",
  CANCELLED: "bg-gray-200 text-gray-900"
};

const OrderDetail = ({ user, onLogout }) => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchingPOD, setFetchingPOD] = useState(false);
  const [fetchingLabel, setFetchingLabel] = useState(null);
  const [fetchingLRCopy, setFetchingLRCopy] = useState(false);
  const [extractingLRInfo, setExtractingLRInfo] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  
  // PO Expiry Date state
  const [editingPOExpiry, setEditingPOExpiry] = useState(false);
  const [poExpiryDate, setPOExpiryDate] = useState("");
  const [savingPOExpiry, setSavingPOExpiry] = useState(false);
  // state(normalised) -> { zone, label } for the origin→dest lane chip
  const [zoneByState, setZoneByState] = useState({});
  
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  // PO Expiry Date handler
  const handleSavePOExpiry = async () => {
    setSavingPOExpiry(true);
    try {
      const response = await axios.put(`${API}/orders/${orderId}/po-expiry-date`, {
        po_expiry_date: poExpiryDate ? new Date(poExpiryDate).toISOString() : null
      });
      if (response.data.success) {
        toast.success("PO Expiry Date updated");
        setOrder(prev => ({ ...prev, po_expiry_date: poExpiryDate ? new Date(poExpiryDate).toISOString() : null }));
        setEditingPOExpiry(false);
      }
    } catch (error) {
      toast.error("Failed to update PO Expiry Date");
    } finally {
      setSavingPOExpiry(false);
    }
  };

  useEffect(() => {
    fetchOrderDetail();
  }, [orderId]);
  
  useEffect(() => {
    if (order?.po_expiry_date) {
      const date = new Date(order.po_expiry_date);
      setPOExpiryDate(date.toISOString().split('T')[0]);
    }
  }, [order?.po_expiry_date]);

  // Zone definition (reference data) → build a state→zone lookup for the lane chip.
  useEffect(() => {
    axios.get(`${API}/rate-validation/zone-definition`)
      .then((r) => {
        const map = {};
        (r.data.zones || []).forEach((z) => {
          (z.states || []).forEach((s) => { map[s.trim().toLowerCase()] = { zone: z.zone, label: z.label }; });
        });
        setZoneByState(map);
      })
      .catch(() => setZoneByState({}));
  }, []);

  const zoneFor = (state) => zoneByState[(state || "").trim().toLowerCase()]?.zone || null;
  // Prefer the stored lane zone (authoritative, set at create/backfill); fall back to the
  // client-side state→zone lookup for orders not yet backfilled.
  const pickupZone = order?.origin_zone || zoneFor(order?.pickup_location?.state);
  const dropZone = order?.dest_zone || zoneFor(order?.drop_location?.state);

  const fetchOrderDetail = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setOrder(response.data);
    } catch (error) {
      toast.error("Failed to fetch order details");
      navigate('/seller/orders');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelShipment = async () => {
    setCancelling(true);
    try {
      const response = await axios.post(`${API}/orders/${orderId}/cancel`);
      if (response.data.success) {
        toast.success(response.data.message || "Shipment cancelled successfully!");
        await fetchOrderDetail();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel shipment");
    } finally {
      setCancelling(false);
    }
  };

  const handleFetchPOD = async () => {
    setFetchingPOD(true);
    try {
      const response = await axios.post(`${API}/orders/${orderId}/fetch-pod`);
      if (response.data.success) {
        toast.success("POD fetched successfully!");
        await fetchOrderDetail();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch POD");
    } finally {
      setFetchingPOD(false);
    }
  };

  const handleDownloadPOD = async () => {
    setFetchingPOD(true);
    try {
      // Call the documents API to get fresh presigned URL
      const response = await axios.get(`${API}/orders/${orderId}/documents`);
      if (response.data.lm_pod_url) {
        window.open(response.data.lm_pod_url, '_blank');
      } else {
        toast.error("POD not available");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to download POD");
    } finally {
      setFetchingPOD(false);
    }
  };

  const handleFetchShippingLabel = async (size) => {
    setFetchingLabel(size);
    try {
      const response = await axios.get(`${API}/orders/${orderId}/shipping-label?size=${size}`);
      if (response.data.url) {
        window.open(response.data.url, '_blank');
        if (!response.data.cached) {
          toast.success(`Shipping label (${size.toUpperCase()}) generated successfully!`);
          await fetchOrderDetail();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to fetch shipping label (${size})`);
    } finally {
      setFetchingLabel(null);
    }
  };

  const handleFetchLRCopy = async () => {
    setFetchingLRCopy(true);
    try {
      const response = await axios.get(`${API}/orders/${orderId}/lr-copy`);
      if (response.data.url) {
        window.open(response.data.url, '_blank');
        if (!response.data.cached) {
          toast.success("LR Copy fetched successfully!");
          await fetchOrderDetail();
        }
      }
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || "Failed to fetch LR Copy";
      if (status === 404) {
        toast.error(detail);
        await fetchOrderDetail();
      } else {
        toast.error(detail);
      }
    } finally {
      setFetchingLRCopy(false);
    }
  };

  const handleExtractLRInfo = async () => {
    setExtractingLRInfo(true);
    try {
      await axios.post(`${API}/orders/${orderId}/extract-lr-copy-info`);
      toast.success("LR Copy info extracted successfully!");
      await fetchOrderDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to extract LR Copy info");
    } finally {
      setExtractingLRInfo(false);
    }
  };

  const handleDownloadDocument = async (docType) => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/download/${docType}`);
      window.open(response.data.download_url, '_blank');
    } catch (error) {
      toast.error("Failed to generate download link");
    }
  };

  if (loading) {
    return (
      <SellerLayout user={user} onLogout={onLogout}>
        <div className="flex justify-center py-12">
          <div className="text-slate-600">Loading order details...</div>
        </div>
      </SellerLayout>
    );
  }

  if (!order) {
    return null;
  }

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/seller/orders')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Orders
          </Button>
          <div className="flex-1">
            <h1 className="text-4xl font-bold text-slate-800">{order.order_number}</h1>
            <p className="text-slate-600">Order Details</p>
            {order.insurance_option && <div className="mt-2"><InsuranceBadge value={order.insurance_option} /></div>}
          </div>
          <Badge className={`${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-800'} border-0 text-base px-4 py-2`}>
            {order.status.replace(/_/g, ' ')}
          </Badge>
          
          {/* Cancel Shipment Button - Show only if order has LRN and status is CREATED or MANIFESTED */}
          {order.lrn && ['CREATED', 'MANIFESTED'].includes(order.status) && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="flex items-center gap-2" disabled={cancelling}>
                  {cancelling ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                  Cancel Shipment
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel Shipment?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will cancel the shipment with Delhivery for LRN: <strong>{order.lrn}</strong>.
                    This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>No, Keep Shipment</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleCancelShipment}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Yes, Cancel Shipment
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          {/* Cancel Order — for orders WITHOUT an LRN (nothing booked with a courier). Cancels in EdgeLMS only. */}
          {!order.lrn && order.status !== 'CANCELLED' && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="flex items-center gap-2" disabled={cancelling} data-testid="cancel-order-btn">
                  {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                  Cancel Order
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel Order?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This order has no LRN, so nothing was booked with any courier — it will be cancelled in EdgeLMS only.
                    This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>No, Keep Order</AlertDialogCancel>
                  <AlertDialogAction onClick={handleCancelShipment} className="bg-red-600 hover:bg-red-700">
                    Yes, Cancel Order
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Order Information */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" style={{color: '#00A699'}} />
                Order Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Order Number</p>
                  <p className="font-semibold text-slate-900">{order.order_number}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">LRN</p>
                  <p className="font-semibold text-slate-900">{order.lrn || "Not Assigned"}</p>
                </div>
                {order.master_waybill && (
                  <div>
                    <p className="text-sm text-slate-500">Master AWB</p>
                    <p className="font-semibold text-slate-900">{order.master_waybill}</p>
                  </div>
                )}
                {order.waybills && order.waybills.length > 0 && (
                  <div>
                    <p className="text-sm text-slate-500">Box AWBs ({order.waybills.length})</p>
                    <div className="space-y-1">
                      {order.waybills.map((awb, index) => (
                        <p key={index} className="font-semibold text-slate-900 text-sm">{awb}</p>
                      ))}
                    </div>
                  </div>
                )}
                {order.doc_waybill && (
                  <div>
                    <p className="text-sm text-slate-500">Document AWB</p>
                    <p className="font-semibold text-slate-900">{order.doc_waybill}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-slate-500">Seller</p>
                  <p className="font-semibold text-slate-900">{order.seller_brand_name || order.seller_id}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">B2B Marketplace</p>
                  {order.b2b_marketplace_name ? (
                    <div className="flex items-center gap-2">
                      {order.b2b_marketplace_logo && (
                        <img src={order.b2b_marketplace_logo} alt={order.b2b_marketplace_name} className="h-5 w-5 object-contain" />
                      )}
                      <p className="font-semibold text-slate-900">{order.b2b_marketplace_name}</p>
                    </div>
                  ) : (
                    <p className="font-semibold text-slate-900">N/A</p>
                  )}
                </div>
                <ReferenceEditRow order={order} onSaved={(patch) => setOrder((prev) => ({ ...prev, ...patch }))} />
                <AppointmentEditRow order={order} onSaved={(patch) => setOrder((prev) => ({ ...prev, ...patch }))} />
                <div>
                  <p className="text-sm text-slate-500">Created Date</p>
                  <p className="font-semibold text-slate-900">
                    {formatDateIST(order.created_at, 'full')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Created By</p>
                  <p className="font-semibold text-slate-900">{order.created_by?.user_name || "N/A"}</p>
                </div>
                {/* PO Expiry Date */}
                <div className="col-span-2 border-t pt-4 mt-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-500">PO Expiry Date</p>
                    {!editingPOExpiry ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingPOExpiry(true)}
                        className="h-6 text-xs"
                      >
                        <Edit2 className="w-3 h-3 mr-1" />
                        {order.po_expiry_date ? 'Edit' : 'Add'}
                      </Button>
                    ) : (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleSavePOExpiry}
                          disabled={savingPOExpiry}
                          className="h-6 text-green-600"
                        >
                          {savingPOExpiry ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingPOExpiry(false);
                            setPOExpiryDate(order.po_expiry_date ? new Date(order.po_expiry_date).toISOString().split('T')[0] : "");
                          }}
                          className="h-6 text-slate-500"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                  {editingPOExpiry ? (
                    <Input
                      type="date"
                      value={poExpiryDate}
                      onChange={(e) => setPOExpiryDate(e.target.value)}
                      className="h-9 mt-1 max-w-[200px]"
                    />
                  ) : (
                    <p className={`font-semibold ${order.po_expiry_date ? 'text-slate-900' : 'text-slate-400 italic'}`}>
                      {order.po_expiry_date ? formatDateIST(order.po_expiry_date, 'short') : 'Not Set'}
                    </p>
                  )}
                </div>
              </div>
              {order.description && (
                <div>
                  <p className="text-sm text-slate-500">Description</p>
                  <p className="text-slate-900">{order.description}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* LR Copy & Info - Right column top */}
          {order.lrn && (
            <Card className="border-0 shadow-lg" data-testid="lr-copy-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" style={{color: '#00A699'}} />
                  LR Copy
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {order.lr_copy_status === 'not_available' && !order.lr_copy_url ? (
                  <div className="flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="lr-copy-not-available">
                    <XCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span className="text-sm text-amber-700">LR Copy Not Available</span>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleFetchLRCopy()}
                    disabled={fetchingLRCopy}
                  >
                    {fetchingLRCopy ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {order.lr_copy_url ? 'Loading...' : 'Fetching LR Copy...'}
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Download LR Copy {!order.lr_copy_url && '(Fetch)'}
                      </>
                    )}
                  </Button>
                )}

                {order.InfoFromPartnerLRCopy ? (
                  <>
                    <LRCopyInfo data={order.InfoFromPartnerLRCopy} />
                    {order.lr_copy_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-center text-slate-500 hover:text-teal-600 hover:border-teal-600"
                        onClick={handleExtractLRInfo}
                        disabled={extractingLRInfo}
                        data-testid="refetch-lr-info-btn"
                      >
                        {extractingLRInfo ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                            Re-extracting...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                            Re-extract Info from LR Copy
                          </>
                        )}
                      </Button>
                    )}
                  </>
                ) : order.lr_copy_url ? (
                  <Button
                    variant="outline"
                    className="w-full justify-start border-teal-600 text-teal-600 hover:bg-teal-50"
                    onClick={handleExtractLRInfo}
                    disabled={extractingLRInfo}
                    data-testid="extract-lr-info-btn"
                  >
                    {extractingLRInfo ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Extracting LR Copy Info...
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-2" />
                        Extract Info from LR Copy
                      </>
                    )}
                  </Button>
                ) : null}
              </CardContent>
            </Card>
          )}

          {/* Invoice Information */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" style={{color: '#00A699'}} />
                Invoice & Shipment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Invoices List */}
              {order.invoices && order.invoices.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-slate-700">Invoices ({order.invoices.length})</p>
                  <div className="border rounded-lg divide-y">
                    {order.invoices.map((inv, index) => (
                      <div key={index} className="flex justify-between items-center p-3">
                        <span className="font-medium text-slate-900">{inv.invoice_number}</span>
                        <span className="text-slate-700">₹{inv.invoice_amount?.toLocaleString()}</span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center p-3 bg-slate-50 font-semibold">
                      <span className="text-slate-700">Total Invoice Amount</span>
                      <span className="text-slate-900">₹{(order.total_invoice_amount || order.invoice_amount || 0).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              ) : order.invoice_number ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Invoice Number</p>
                    <p className="font-semibold text-slate-900">{order.invoice_number}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Invoice Amount</p>
                    <p className="font-semibold text-slate-900">₹{(order.total_invoice_amount || order.invoice_amount || 0).toLocaleString()}</p>
                  </div>
                </div>
              ) : null}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Payment Mode</p>
                  <p className="font-semibold text-slate-900">{order.payment_mode}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Number of Boxes</p>
                  <p className="font-semibold text-slate-900">{order.number_of_boxes}</p>
                </div>
                {!order.skip_eway_bill && order.eway_bill_number && (
                  <div className="col-span-2">
                    <p className="text-sm text-slate-500">E-Way Bill Number</p>
                    <p className="font-semibold text-slate-900">{order.eway_bill_number}</p>
                  </div>
                )}
              </div>
              <div className="border-t pt-4 space-y-2">
                {order.invoice_document_path && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleDownloadDocument('invoice')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Invoice Document
                  </Button>
                )}
                {order.secondary_document_path && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleDownloadDocument('secondary')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Secondary Document
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Shipping Documents */}
          {order.lrn && (
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" style={{color: '#00A699'}} />
                  Shipping Documents
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Shipping Labels Section */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">Shipping Labels</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { size: 'sm', label: '3" x 2"' },
                        { size: 'md', label: '4" x 2"' },
                        { size: 'a4', label: 'A4 Size' },
                        { size: 'std', label: '4" x 2.5"' }
                      ].map(({ size, label }) => {
                        const hasLabel = order[`shipping_label_${size}_url`];
                        return (
                          <Button
                            key={size}
                            variant="outline"
                            className="justify-start"
                            onClick={() => handleFetchShippingLabel(size)}
                            disabled={fetchingLabel === size}
                          >
                            {fetchingLabel === size ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                {hasLabel ? 'Loading...' : 'Generating...'}
                              </>
                            ) : (
                              <>
                                <Download className="w-4 h-4 mr-2" />
                                {label} {hasLabel ? '' : '(Generate)'}
                              </>
                            )}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* POD */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">Proof of Delivery (POD)</h4>
                    {order.lm_pod_url ? (
                      <Button
                        variant="outline"
                        className="w-full justify-start"
                        onClick={handleDownloadPOD}
                        disabled={fetchingPOD}
                      >
                        {fetchingPOD ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            Download POD
                          </>
                        )}
                      </Button>
                    ) : order.status === 'DELIVERED' ? (
                      <Button
                        variant="outline"
                        className="w-full justify-start border-teal-600 text-teal-600 hover:bg-teal-50"
                        onClick={handleFetchPOD}
                        disabled={fetchingPOD}
                      >
                        {fetchingPOD ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Fetching POD...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Fetch POD from Shipping Partner
                          </>
                        )}
                      </Button>
                    ) : (
                      <p className="text-sm text-slate-500">POD available after delivery</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Box Details */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" style={{color: '#00A699'}} />
                Box Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {order.boxes && order.boxes.length > 0 ? order.boxes.map((box, index) => (
                  <div key={index} className="border rounded-lg p-4 bg-slate-50">
                    <p className="font-semibold text-slate-700 mb-2">Box {index + 1}</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-slate-500">Dimensions: </span>
                        <span className="text-slate-900">{box.length} × {box.width} × {box.height} cm</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Weight: </span>
                        <span className="text-slate-900">{box.weight} kg</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-slate-500">Quantity: </span>
                        <span className="text-slate-900">{box.quantity}</span>
                      </div>
                    </div>
                  </div>
                )) : (
                  <p className="text-slate-500 text-center py-4">No box details available</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* LR Charges */}
          {order.seller_charges && (
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" style={{color: '#00A699'}} />
                  LR Charges
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Total Amount Box */}
                  <div className="bg-gradient-to-r from-[#00A699] to-[#008080] rounded-lg p-4 text-white">
                    <p className="text-sm opacity-90">Total Amount Charged</p>
                    <p className="text-3xl font-bold">
                      ₹{order.seller_charges.final_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    {order.seller_charges.charged_wt_used && (
                      <div className="mt-3 pt-3 border-t border-white/20">
                        <span className="text-sm opacity-90">Charged Weight: </span>
                        <span className="font-semibold">{order.seller_charges.charged_wt_used} kg</span>
                      </div>
                    )}
                    {order.wallet_deducted_at && (
                      <p className="text-xs mt-2 opacity-75">
                        Deducted from wallet on {formatDateIST(order.wallet_deducted_at, 'full')}
                      </p>
                    )}
                  </div>

                  {/* Freight re-pull adjustments — clearly show old vs new charge and what
                      was additionally debited / credited back after the carrier revised the freight. */}
                  {Array.isArray(order.freight_pull_history) && order.freight_pull_history.some(h => h.wallet_adjustment) && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                      <h4 className="font-semibold text-amber-900 text-sm mb-1 flex items-center gap-2">
                        <RefreshCw className="w-4 h-4" /> Charge revised after re-pull
                      </h4>
                      <p className="text-xs text-amber-800 mb-3">
                        The shipping partner revised the freight for this shipment. We re-pulled the latest
                        charge and adjusted your wallet by the difference.
                      </p>
                      <div className="space-y-2">
                        {order.freight_pull_history.filter(h => h.wallet_adjustment).map(h => {
                          const adj = h.wallet_adjustment;
                          const m = (v) => (v == null ? "0.00" : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
                          return (
                            <div key={h.id} className="rounded bg-white border border-amber-100 p-3 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-600">Charge revised</span>
                                <span className="tabular-nums">₹{m(adj.previous_amount)} → <span className="font-semibold">₹{m(adj.new_amount)}</span></span>
                              </div>
                              <div className="flex justify-between mt-1">
                                <span className="text-slate-600">{adj.type === 'debit' ? 'Additional amount debited' : 'Amount credited back'}</span>
                                <span className={`font-semibold ${adj.type === 'debit' ? 'text-red-600' : 'text-green-600'}`}>
                                  {adj.type === 'debit' ? '−' : '+'}₹{m(adj.amount)}
                                </span>
                              </div>
                              {h.pulled_at && <p className="text-xs text-slate-400 mt-1">on {formatDateIST(h.pulled_at, 'full')}</p>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Forward Charges Breakdown */}
                  {order.seller_charges.fwd_charges && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-slate-100 px-4 py-2 border-b">
                        <h4 className="font-semibold text-slate-800">Forward Charges Breakdown</h4>
                      </div>
                      <div className="p-4 space-y-3">
                        {/* Charge Parameters */}
                        {order.seller_charges.fwd_charges.breakdown && Object.entries(order.seller_charges.fwd_charges.breakdown).map(([param, details]) => (
                          details.seller_value > 0 && (
                            <div key={param} className="flex justify-between items-center py-2 border-b last:border-b-0">
                              <span className="text-sm text-slate-600 capitalize">{param.replace(/_/g, ' ')}</span>
                              <span className="font-semibold text-slate-900">₹{details.seller_value?.toFixed(2)}</span>
                            </div>
                          )
                        ))}

                        {/* Pre-Tax Total */}
                        <div className="flex justify-between items-center py-2 bg-slate-50 px-3 rounded font-semibold">
                          <span className="text-slate-700">Pre-Tax Freight</span>
                          <span className="text-slate-900">₹{order.seller_charges.fwd_charges.pre_tax_freight?.toFixed(2)}</span>
                        </div>

                        {/* GST */}
                        <div className="flex justify-between items-center py-2">
                          <span className="text-sm text-slate-600">GST ({order.seller_charges.fwd_charges.gst_percent}%)</span>
                          <span className="font-semibold text-slate-900">₹{order.seller_charges.fwd_charges.gst?.toFixed(2)}</span>
                        </div>

                        {/* Forward Total */}
                        <div className="flex justify-between items-center py-2 bg-green-50 px-3 rounded font-bold">
                          <span className="text-green-800">Forward Charges Total</span>
                          <span className="text-green-900">₹{order.seller_charges.fwd_charges.total?.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* RTO Charges (if applicable) */}
                  {order.seller_charges.rto_charges && order.seller_charges.rto_charges.total > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-red-100 px-4 py-2 border-b">
                        <h4 className="font-semibold text-red-800">RTO Charges Breakdown</h4>
                      </div>
                      <div className="p-4 space-y-3">
                        {order.seller_charges.rto_charges.breakdown && Object.entries(order.seller_charges.rto_charges.breakdown).map(([param, details]) => (
                          details.seller_value > 0 && (
                            <div key={param} className="flex justify-between items-center py-2 border-b last:border-b-0">
                              <span className="text-sm text-slate-600 capitalize">{param.replace(/_/g, ' ')}</span>
                              <span className="font-semibold text-slate-900">₹{details.seller_value?.toFixed(2)}</span>
                            </div>
                          )
                        ))}

                        <div className="flex justify-between items-center py-2 bg-red-50 px-3 rounded font-bold">
                          <span className="text-red-800">RTO Charges Total</span>
                          <span className="text-red-900">₹{order.seller_charges.rto_charges.total?.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Management Fee (if applicable) */}
                  {order.seller_charges.management_fee && order.seller_charges.management_fee.enabled && (
                    <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-amber-800">Management Fee ({order.seller_charges.management_fee.percentage}%)</span>
                        <span className="font-semibold text-amber-900">₹{order.seller_charges.management_fee.amount?.toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  {/* Final Amount Summary */}
                  <div className="bg-slate-800 rounded-lg p-4 text-white">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold">Final Amount</span>
                      <span className="text-2xl font-bold">₹{order.seller_charges.final_amount?.toFixed(2)}</span>
                    </div>
                    <p className="text-xs mt-1 opacity-75">Inclusive of all charges and taxes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Locations */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center justify-between gap-2 flex-wrap">
                <span className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" style={{color: '#00A699'}} />
                  Pickup & Drop Locations
                </span>
                {pickupZone && dropZone && (
                  <a
                    href="/seller/zone-definition"
                    title="Shipping zone of the pickup and drop states — see Zone Definition"
                    className="inline-flex items-center gap-1 rounded-full bg-[#00A699]/10 text-[#00A699] text-xs font-medium px-2.5 py-0.5 hover:bg-[#00A699]/20"
                  >
                    {pickupZone} <span className="text-slate-400">→</span> {dropZone}
                  </a>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Pickup Location */}
              {order.pickup_location && (
                <div className="border rounded-lg p-4 bg-green-50">
                  <div className="flex items-center gap-2 mb-2">
                    <Truck className="w-4 h-4 text-green-600" />
                    <p className="font-semibold text-green-800">Pickup Location</p>
                  </div>
                  <p className="font-medium text-slate-900">{order.pickup_location.facility_name}</p>
                  <p className="text-sm text-slate-600">{order.pickup_location.address_line}</p>
                  <p className="text-sm text-slate-600">
                    {order.pickup_location.city}, {order.pickup_location.state} - {order.pickup_location.pincode}
                  </p>
                  <p className="text-sm mt-1 flex items-center gap-1.5">
                    <span className="text-slate-500">Zone:</span>
                    <span className="inline-flex items-center rounded bg-[#00A699]/10 text-[#00A699] text-xs font-semibold px-2 py-0.5">{pickupZone || "—"}</span>
                  </p>
                  <div className="mt-2 text-sm">
                    <p className="text-slate-700">Contact: {order.pickup_location.contact_person_name}</p>
                    <p className="text-slate-600">{order.pickup_location.contact_phone}</p>
                  </div>
                </div>
              )}

              {/* Drop Location */}
              {order.drop_location && (
                <div className="border rounded-lg p-4 bg-blue-50">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-blue-600" />
                    <p className="font-semibold text-blue-800">Drop Location</p>
                  </div>
                  <p className="font-medium text-slate-900">{order.drop_location.company_name}</p>
                  <p className="text-sm text-slate-600">{order.drop_location.address_line}</p>
                  <p className="text-sm text-slate-600">
                    {order.drop_location.city}, {order.drop_location.state} - {order.drop_location.pincode}
                  </p>
                  <p className="text-sm mt-1 flex items-center gap-1.5">
                    <span className="text-slate-500">Zone:</span>
                    <span className="inline-flex items-center rounded bg-[#00A699]/10 text-[#00A699] text-xs font-semibold px-2 py-0.5">{dropZone || "—"}</span>
                  </p>
                  <div className="mt-2 text-sm">
                    <p className="text-slate-700">Contact: {order.drop_location.contact_person_name}</p>
                    <p className="text-slate-600">{order.drop_location.drop_contact}</p>
                    {order.drop_location.email && (
                      <p className="text-slate-600">{order.drop_location.email}</p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Edit History (pre-LRN order edits) */}
        {order.edit_history && order.edit_history.length > 0 && (
          <div className="mt-6">
            <OrderEditHistory history={order.edit_history} />
          </div>
        )}

        {/* Estimated & Promised Delivery Dates */}
        {((order.estimated_dates && order.estimated_dates.length > 0) ||
          (order.promised_delivery_dates && order.promised_delivery_dates.length > 0)) && (
          <div className="grid grid-cols-2 gap-6 mt-6">
            {order.estimated_dates && order.estimated_dates.length > 0 && (
              <Card className="border-0 shadow-lg bg-blue-50">
                <CardContent className="pt-4">
                  <p className="text-sm font-semibold text-blue-800 mb-2">Estimated Delivery Dates</p>
                  <div className="space-y-2">
                    {order.estimated_dates.map((entry, idx) => {
                      const dateValue = entry?.date || entry;
                      return (
                        <div key={idx} className="text-sm">
                          <p className="text-blue-700 font-medium">{formatDateIST(dateValue, 'full')}</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
            {order.promised_delivery_dates && order.promised_delivery_dates.length > 0 && (
              <Card className="border-0 shadow-lg bg-green-50">
                <CardContent className="pt-4">
                  <p className="text-sm font-semibold text-green-800 mb-2">Promised Delivery Dates</p>
                  <div className="space-y-2">
                    {order.promised_delivery_dates.map((entry, idx) => {
                      const dateValue = entry?.date || entry;
                      return (
                        <div key={idx} className="text-sm">
                          <p className="text-green-700 font-medium">{formatDateIST(dateValue, 'full')}</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Combined Order Tracking Journey */}
        <Card className="border-0 shadow-lg mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" style={{color: '#00A699'}} />
              Order Tracking Journey
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Status Timeline - only show event time if available */}
            <div className="relative">
              {(order.status_history || []).slice().reverse().map((entry, index) => {
                const isLast = index === (order.status_history?.length || 0) - 1;
                const statusColor = STATUS_COLORS[entry.status] || 'bg-gray-100 text-gray-800';
                
                return (
                  <div key={index} className="flex gap-4 pb-6 relative">
                    {/* Timeline connector */}
                    {!isLast && (
                      <div className="absolute left-[11px] top-6 w-0.5 h-full bg-slate-200" />
                    )}
                    
                    {/* Status dot */}
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                      index === 0 ? 'bg-[#00A699]' : 'bg-slate-300'
                    }`}>
                      {index === 0 ? (
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      ) : (
                        <div className="w-2 h-2 rounded-full bg-white" />
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={`${statusColor} border-0`}>
                          {entry.status?.replace(/_/g, ' ')}
                        </Badge>
                        {entry.event_timestamp && (
                          <span className="text-sm text-slate-600">
                            {formatDateIST(entry.event_timestamp, 'full')}
                          </span>
                        )}
                      </div>
                      {entry.remarks && (
                        <p className="text-sm text-slate-500 mt-1 italic">{entry.remarks}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Scan History Table - if available */}
            {order.scan_history && order.scan_history.length > 0 && (
              <div className="mt-6 pt-6 border-t">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Courier Scan Details</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Location</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Remark</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Time</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {order.scan_history.slice().reverse().map((scan, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-4 py-3 align-middle">
                            <Badge variant="outline" className="text-xs">
                              {scan.status?.replace(/_/g, ' ')}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 align-middle text-slate-700">{scan.location || '-'}</td>
                          <td className="px-4 py-3 align-middle text-slate-600 italic">{scan.scan_remark || '-'}</td>
                          <td className="px-4 py-3 align-middle text-slate-700">
                            {scan.scan_timestamp ? (
                              <div className="whitespace-nowrap">
                                <div>{formatDateIST(scan.scan_timestamp, 'short')}</div>
                                <div className="text-xs text-slate-500">{formatDateIST(scan.scan_timestamp, 'full').split(', ').pop()}</div>
                              </div>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Video Footage Card - Only shown if videos exist */}
        {((order.pickup_videos && order.pickup_videos.length > 0) || (order.delivery_videos && order.delivery_videos.length > 0)) && (
          <Card className="border-0 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-rose-50 to-orange-50 border-b">
              <CardTitle className="flex items-center gap-2 text-lg text-rose-800">
                <Video className="w-5 h-5" />
                Video Footage
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Pickup Videos */}
                {order.pickup_videos && order.pickup_videos.length > 0 && (
                  <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-lg p-4 border border-blue-100">
                    <h4 className="font-semibold text-blue-800 flex items-center gap-2 mb-4">
                      <Truck className="w-4 h-4" />
                      Pickup Videos
                    </h4>
                    <div className="space-y-2">
                      {order.pickup_videos.map((video, idx) => (
                        <div
                          key={video.id || idx}
                          className="flex items-center justify-between bg-white rounded-lg p-3 border"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                              <Play className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium truncate">{video.filename}</p>
                              <p className="text-xs text-slate-500">
                                {formatFileSize(video.size)} • {formatDateIST(video.uploaded_at, 'short')}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            asChild
                            className="h-8 w-8 p-0"
                          >
                            <a href={video.url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-4 h-4 text-blue-600" />
                            </a>
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Delivery Videos */}
                {order.delivery_videos && order.delivery_videos.length > 0 && (
                  <div className="bg-gradient-to-br from-green-50 to-slate-50 rounded-lg p-4 border border-green-100">
                    <h4 className="font-semibold text-green-800 flex items-center gap-2 mb-4">
                      <MapPin className="w-4 h-4" />
                      Delivery Videos
                    </h4>
                    <div className="space-y-2">
                      {order.delivery_videos.map((video, idx) => (
                        <div
                          key={video.id || idx}
                          className="flex items-center justify-between bg-white rounded-lg p-3 border"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                              <Play className="w-5 h-5 text-green-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium truncate">{video.filename}</p>
                              <p className="text-xs text-slate-500">
                                {formatFileSize(video.size)} • {formatDateIST(video.uploaded_at, 'short')}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            asChild
                            className="h-8 w-8 p-0"
                          >
                            <a href={video.url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-4 h-4 text-green-600" />
                            </a>
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </SellerLayout>
  );
};

export default OrderDetail;
